import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { useKFlags } from '@/hooks/use-k-flags';
import { useGetRoutes } from './components/getRoutes';
import { routeFamily } from './routeFamily';
import { useStaleBuildCheck } from '@/hooks/useStaleBuildCheck';
import { useHasUpgradeHolds } from '@/hooks/useUpgradeHold';
import { warmBuild } from '@/lib/prefetchBuild';
import { StaleBuildPrompt } from './StaleBuildPrompt';

/**
 * Moves a tab onto a newer build without anyone asking for a refresh.
 *
 * A running SPA cannot swap its module graph, so an upgrade is always a
 * document reload — the job here is making that reload one nobody notices:
 *
 * - The moment a newer build is detected, its entry graph is preloaded so
 *   the eventual reload is served from cache (`prefetchBuild`).
 * - The reload happens at a page boundary: the next move to a different
 *   page, or after the tab has sat hidden for a minute. Never mid-task —
 *   anything with work in flight registers a hold (`useUpgradeHold`) and the
 *   reload waits.
 * - It listens to history rather than blocking it. `history` v4 allows one
 *   blocker, and the unsaved-changes guards (`useUnsavedChangesWarning`,
 *   `<Prompt>`) already own it; a listener fires only after they have let
 *   the transition through, so a cancelled "leave this form?" never reloads.
 *
 * `StaleBuildPrompt` remains as the fallback for a tab that could not be
 * upgraded quietly, and as the only behaviour when `feSilentUpgrade` is off.
 */

/** How long a hidden tab stays hidden before it is safe to reload under it. */
export const HIDDEN_RELOAD_DELAY_MS = 60 * 1000;
/** How long a stale tab waits for a quiet upgrade before it is asked instead. */
export const FALLBACK_AFTER_MS = 30 * 60 * 1000;
const ATTEMPTED_STORAGE_KEY = 'kula:upgrade-attempted-for';

type Trigger = 'navigation' | 'hidden';

/**
 * The build id the last automatic reload aimed for. If this tab booted on a
 * different build and is told the same id is still the latest, the reload
 * did not take — a proxy or an edge is still serving the old shell — and
 * reloading again would loop forever. Session-scoped: a new tab starts clean.
 */
const readAttemptedFor = (): string | null => {
  try {
    return window.sessionStorage.getItem(ATTEMPTED_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeAttemptedFor = (buildId: string): void => {
  try {
    window.sessionStorage.setItem(ATTEMPTED_STORAGE_KEY, buildId);
  } catch {
    // Without storage the loop guard does not survive the reload; the
    // fallback prompt still bounds the damage to one extra reload per tab.
  }
};

const report = (event: string, data: Record<string, unknown>) => {
  Sentry.addBreadcrumb({ category: 'build', message: event, data });
  posthog.capture(event, data);
};

export const UpgradeManager = () => {
  const { feStaleBuildNudge, feSilentUpgrade } = useKFlags() as {
    feStaleBuildNudge?: boolean;
    feSilentUpgrade?: boolean;
  };
  const silent = Boolean(feSilentUpgrade) && !import.meta.env.DEV;
  const nudge = Boolean(feStaleBuildNudge) && !import.meta.env.DEV;

  const history = useHistory();
  const { pathname } = useLocation();
  // Which page this is, not which URL. A page that routes its own tabs
  // (flow steps/settings/candidates) changes the pathname without the
  // reader going anywhere — see routeFamily.
  const routes = useGetRoutes();
  const pageKey = routeFamily(pathname, routes);
  const { isStale, latestBuildId, staleSince, reload } = useStaleBuildCheck({
    enabled: silent || nudge,
    pageKey,
  });
  const held = useHasUpgradeHolds();

  // A reload that did not take must not be retried for the same target.
  const [loopBlocked, setLoopBlocked] = useState(false);
  useEffect(() => {
    if (!latestBuildId) return;
    const attempted = readAttemptedFor();
    if (attempted === latestBuildId && !loopBlocked) {
      setLoopBlocked(true);
      report('build.upgrade_loop_blocked', {
        booted: import.meta.env.VITE_RELEASE,
        latest: latestBuildId,
      });
    }
  }, [latestBuildId, loopBlocked]);

  // Warm once per target build. Re-runs if a second deploy moves the target.
  const warmedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!silent || !latestBuildId || warmedFor.current === latestBuildId)
      return;
    warmedFor.current = latestBuildId;
    warmBuild(latestBuildId);
  }, [silent, latestBuildId]);

  // Read through refs from the history listener and the hidden timer, so a
  // subscription made once sees current state without resubscribing.
  const state = useRef({
    silent,
    isStale,
    held,
    loopBlocked,
    latestBuildId,
    routes,
  });
  state.current = { silent, isStale, held, loopBlocked, latestBuildId, routes };

  const attempt = useCallback(
    (trigger: Trigger) => {
      const s = state.current;
      if (!s.silent || !s.isStale || s.held || s.loopBlocked) return false;
      if (!s.latestBuildId) return false;
      writeAttemptedFor(s.latestBuildId);
      report('build.upgraded', {
        from: import.meta.env.VITE_RELEASE,
        to: s.latestBuildId,
        trigger,
      });
      reload();
      return true;
    },
    [reload]
  );

  // Page boundary: the reader moved to a different page and the transition
  // was allowed. Compared by route, not pathname — moving between a page's
  // own tabs is not leaving it.
  const lastPageKey = useRef(pageKey);
  useEffect(() => {
    const unlisten = history.listen((location) => {
      const next = routeFamily(location.pathname, state.current.routes);
      if (next === lastPageKey.current) return;
      lastPageKey.current = next;
      attempt('navigation');
    });
    return unlisten;
  }, [history, attempt]);

  // Hidden long enough that nobody is looking.
  useEffect(() => {
    let timer: number | undefined;
    const onVisibility = () => {
      window.clearTimeout(timer);
      if (document.visibilityState !== 'hidden') return;
      timer = window.setTimeout(() => {
        if (document.visibilityState === 'hidden') attempt('hidden');
      }, HIDDEN_RELOAD_DELAY_MS);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [attempt]);

  // Ask only when asking is all we can do: the silent path is off, or this
  // tab has been stale for a while and still could not be upgraded.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!silent || !isStale) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, [silent, isStale]);
  const waitedTooLong =
    staleSince !== null && now - staleSince >= FALLBACK_AFTER_MS;
  const promptStale =
    nudge && isStale && (!silent || loopBlocked || waitedTooLong);

  return <StaleBuildPrompt isStale={promptStale} reload={reload} />;
};
